import { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Textarea,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconMail, IconSend } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Carrier, Client, Order } from "@/types/api";

// SendPrintDialog — диалог отправки заявки на email.
// Требование ТЗ 01.07.2026 п.1: «при отправке спрашивает — кому отправляем,
// смотрит список контактных лиц». В MVP: 1 email из карточки контрагента
// предзаполнен, можно поправить + добавить копии.

interface Props {
  order: Order;
  kind: "customer" | "carrier";
  opened: boolean;
  onClose: () => void;
}

export function SendPrintDialog({ order, kind, opened, onClose }: Props) {
  const contragentName = kind === "customer" ? order.client_name : order.carrier_name;
  // Дефолтные значения: email контрагента (пока в Order у нас его нет — оставляем
  // пусто, пользователь заполняет). Тема автогенерируется.
  const defaultSubject = `Заявка на перевозку №${order.number_internal} от ${(order.document_date || "").slice(0, 10).split("-").reverse().join(".")}`;

  const [recipient, setRecipient] = useState("");
  const [cc, setCC] = useState("");
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState("");

  // Подтягиваем email из карточки контрагента (ТЗ п.1: «смотрит список
  // контактных лиц»; пока у контрагента одно поле email — предзаполняем его).
  const contragentID = kind === "customer" ? order.client_id : order.carrier_id;
  const contragentQ = useQuery({
    queryKey: [kind === "customer" ? "clients" : "carriers", contragentID, "email"],
    queryFn: async () => {
      const path = kind === "customer" ? "clients" : "carriers";
      const r = await api.get<Client | Carrier>(`/api/v1/${path}/${contragentID}`);
      return r.data;
    },
    enabled: opened && !!contragentID,
    staleTime: 60_000,
  });

  // Сброс при открытии.
  useEffect(() => {
    if (opened) {
      setRecipient("");
      setCC("");
      setSubject(defaultSubject);
      setBody("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, kind, order.id]);

  // Когда карточка контрагента загрузилась — предзаполняем email,
  // но не перетираем то, что пользователь уже начал вводить.
  useEffect(() => {
    const email = contragentQ.data?.email;
    if (opened && email && recipient === "") {
      setRecipient(email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contragentQ.data, opened]);

  const sendMut = useMutation({
    mutationFn: async () => {
      const r = await api.post(`/api/v1/orders/${order.id}/send-print`, {
        kind,
        recipient,
        subject,
        body,
        cc: cc
          .split(/[,;\s]+/)
          .map((s) => s.trim())
          .filter(Boolean),
      });
      return r.data;
    },
    onSuccess: () => {
      notifications.show({
        title: "Отправлено",
        message: `Заявка отправлена на ${recipient}`,
        color: "teal",
      });
      onClose();
    },
    onError: (e: unknown) => {
      const err = e as { response?: { status?: number; data?: { message?: string } } };
      if (err.response?.status === 503) {
        notifications.show({
          title: "SMTP не настроен",
          message:
            err.response.data?.message ??
            "Заполните SMTP-настройки в карточке своей фирмы.",
          color: "yellow",
        });
        return;
      }
      notifications.show({
        title: "Не удалось отправить",
        message: err.response?.data?.message ?? "Проверьте адрес и SMTP-настройки.",
        color: "red",
      });
    },
  });

  const kindLabel = kind === "customer" ? "заказчику" : "перевозчику";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconMail size={20} />
          <span>Отправка заявки {kindLabel}</span>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        {contragentName && (
          <Alert variant="light" color="blue" title={`Адресат: ${contragentName}`}>
            Заявка №{order.number_internal}. Файл docx приложится автоматически.
          </Alert>
        )}
        {!contragentName && (
          <Alert variant="light" color="yellow" icon={<IconAlertTriangle size={16} />}>
            {kind === "customer" ? "Заказчик" : "Перевозчик"} не выбран в заявке —
            заполните email вручную.
          </Alert>
        )}
        <TextInput
          label="Кому (email)"
          placeholder="contact@example.ru"
          required
          value={recipient}
          onChange={(e) => setRecipient(e.currentTarget.value.trim())}
        />
        <TextInput
          label="Копия (через запятую)"
          placeholder="cc1@example.ru, cc2@example.ru"
          value={cc}
          onChange={(e) => setCC(e.currentTarget.value)}
        />
        <TextInput
          label="Тема"
          value={subject}
          onChange={(e) => setSubject(e.currentTarget.value)}
        />
        <Textarea
          label="Сообщение"
          placeholder="Пусто = стандартный шаблон"
          autosize
          minRows={4}
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={onClose}>
            Отмена
          </Button>
          <Button
            leftSection={<IconSend size={16} />}
            loading={sendMut.isPending}
            disabled={!recipient}
            onClick={() => sendMut.mutate()}
          >
            Отправить
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
